import React, { useState, useMemo } from 'react';
import { Sankey, Tooltip, ResponsiveContainer } from 'recharts';
import ReportShell from './ReportShell';
import useReport from './useReport';
import toSankey, { sankeyHeight, sankeyNodePadding } from './charts/toSankey';

// Definitions sourced from backend/app/services/profile_completeness.py's
// SECTIONS semantics (see PLAN4A-OUTCOMES.md §3) -- the two sections whose
// plain-English name overstates what they actually measure. "measurables"
// reads as "the sport's full required set" but is really >=1 measurable;
// "competition_record" reads as "a season" but is really >=1 row in ANY
// ledger. Rendered as visible text, not a tooltip -- an owner skimming the
// table would otherwise assume the stronger meaning.
const SECTION_DEFINITIONS = {
  measurables:
    'at least one measurable recorded from the athlete’s sport catalog set '
    + '— not the sport’s full required set.',
  competition_record:
    'at least one row in any competition ledger (game stats, results, event '
    + 'marks, matches, or placements) — any single ledger counts.',
};

export default function ProfileBuildPathReport() {
  const [population, setPopulation] = useState('organic');
  const { data, loading, error } = useReport('profile-build', population);
  const sankey = useMemo(
    () => toSankey(data?.build_path?.transitions),
    [data],
  );

  return (
    <ReportShell
      title="Profile Completion & Build Path"
      headline={data?.headline}
      population={population}
      onPopulationChange={setPopulation}
      populationSize={data?.population_size ?? 0}
      thin={data?.thin}
      loading={loading}
      error={error}
    >
      <h3>Completion by section</h3>
      {/*
       * Section `rate` is NOT computed over `population_size` (the header
       * denominator above). The backend divides by `scored_athletes` --
       * the count of rows in the latest daily completeness snapshot, i.e.
       * one row per athlete PROFILE that has been scored so far. The
       * header count is one row per athlete USER matching the population
       * filter, whether or not a snapshot has scored them yet. The two
       * numbers differ (scored_athletes <= population_size) and must stay
       * visibly distinct -- do not "simplify" this by reusing
       * population_size here, that is the exact silent-denominator-swap
       * bug this line exists to prevent.
       */}
      {data?.snapshot_day ? (
        <p className="report-note">
          Section rates are of the <strong>{data.scored_athletes}</strong> athletes
          scored in the snapshot taken <strong>{data.snapshot_day}</strong>.
        </p>
      ) : (
        <p className="report-note">No completeness snapshot has run yet.</p>
      )}
      {data?.wall && (
        <p className="report-note">
          The wall is <strong>{data.wall}</strong> — the least-completed section.
        </p>
      )}
      <div className="report-table-wrap">
        <table className="report-table">
          <thead><tr><th>Section</th><th>Complete</th><th>Rate</th></tr></thead>
          <tbody>
            {(data?.sections || []).map((s) => (
              <tr key={s.section}>
                <td>{s.section}</td><td>{s.complete}</td>
                <td>{Math.round(s.rate * 100)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <dl className="report-definitions">
        {Object.entries(SECTION_DEFINITIONS).map(([section, def]) => (
          <div key={section}>
            <dt>{section}</dt>
            <dd>{def}</dd>
          </div>
        ))}
      </dl>

      <h3>What athletes do first</h3>
      <p className="report-note">
        Build order below (first actions, build order, and stop points) is
        <strong> all-time</strong> — it is not limited by the snapshot day above.
      </p>
      <div className="report-table-wrap">
        <table className="report-table">
          <thead><tr><th>First action</th><th>Athletes</th></tr></thead>
          <tbody>
            {Object.entries(data?.build_path?.first_action_counts || {})
              .sort((a, b) => b[1] - a[1])
              .map(([screen, count]) => (
                <tr key={screen}><td>{screen}</td><td>{count}</td></tr>
              ))}
          </tbody>
        </table>
      </div>

      <h3>Build order</h3>
      {sankey.links.length > 0 ? (
        <>
          <div style={{ width: '100%', height: sankeyHeight(sankey.nodes.length, 400) }}>
            <ResponsiveContainer>
              <Sankey data={sankey} nodePadding={sankeyNodePadding(sankey.nodes.length)} link={{ stroke: '#0066CC' }}>
                <Tooltip />
              </Sankey>
            </ResponsiveContainer>
          </div>
          {sankey.droppedCount > 0 && (
            <p className="report-note">
              {sankey.droppedCount} return {sankey.droppedCount === 1 ? 'path' : 'paths'} hidden
              to keep the flow readable.
            </p>
          )}
        </>
      ) : (
        <p className="report-note">Not enough ordered actions yet to draw a path.</p>
      )}

      <h3>Where athletes stop</h3>
      <div className="report-table-wrap">
        <table className="report-table">
          <thead><tr><th>Last action</th><th>Athletes</th></tr></thead>
          <tbody>
            {Object.entries(data?.build_path?.stop_points || {})
              .sort((a, b) => b[1] - a[1])
              .map(([screen, count]) => (
                <tr key={screen}><td>{screen}</td><td>{count}</td></tr>
              ))}
          </tbody>
        </table>
      </div>

      {data?.payoff_note && <p className="report-note">{data.payoff_note}</p>}
    </ReportShell>
  );
}
